import { useEffect, useRef } from 'react';
import { isAxiosError } from 'axios';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { useProfile } from '@digiwedge/hooks-auth-web';
import { completePlayerRegistration } from '@digiwedge/teetime-api-client';
import { resolveAvatarUrl, resolveDisplayName } from '@digiwedge/tee-time-ui';

export const useAccountBootstrap = () => {
  const isAuthenticated = useGlobalStore((state: GlobalState) => state.isAuthenticated);
  const setProfile = useGlobalStore((state: GlobalState) => state.setProfile);
  const clearProfile = useGlobalStore((state: GlobalState) => state.clearProfile);
  const logout = useGlobalStore((state: GlobalState) => state.logout);
  const setMfaEnrolled = useGlobalStore((state: GlobalState) => state.setMfaEnrolled);
  const setMfaSessionVerified = useGlobalStore((state: GlobalState) => state.setMfaSessionVerified);
  const { fetchProfile } = useProfile();
  const inFlightRef = useRef(false);
  const completedPlayerRegistrationRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      clearProfile();
      setMfaEnrolled(null);
      setMfaSessionVerified(false);
      return;
    }

    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;

    (async () => {
      try {
        setMfaEnrolled(null);
        setMfaSessionVerified(false);
        const profileData = await fetchProfile();
        if (!profileData) {
          setMfaEnrolled(false);
          setMfaSessionVerified(true);
          return;
        }
        const resolvedName = resolveDisplayName(profileData);
        setProfile({
          userId: profileData.userId,
          name: resolvedName,
          displayName: profileData.displayName ?? resolvedName,
          email: profileData.email,
          avatarUrl: resolveAvatarUrl(profileData.avatar),
        });
        if (!completedPlayerRegistrationRef.current.has(profileData.userId)) {
          completedPlayerRegistrationRef.current.add(profileData.userId);
          void completePlayerRegistration().catch(() => undefined);
        }
        const hasMfaEnrolled = Boolean(profileData.mfa?.enrolled);
        const hasSessionVerification = hasMfaEnrolled
          ? Boolean(profileData.mfa?.lastVerifiedAt)
          : true;
        setMfaEnrolled(hasMfaEnrolled);
        setMfaSessionVerified(hasSessionVerification);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          logout();
          return;
        }
        setMfaEnrolled(false);
        setMfaSessionVerified(false);
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [
    isAuthenticated,
    fetchProfile,
    setProfile,
    clearProfile,
    setMfaEnrolled,
    setMfaSessionVerified,
    logout,
  ]);
};
